import { Link } from "react-router-dom";
import {
    ArrowRight,
    ArrowUpDown,
    CalendarCheck,
    Headset,
    MapPin,
    Settings,
    ShieldCheck,
    TrendingUp,
    Wrench,
    Zap,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { IMG, LOCATIONS } from "@/lib/site";

const APPROACH = [
    {
        icon: ShieldCheck,
        title: "SAFETY FIRST",
        text: "Keeping safety at the center of elevator service.",
    },
    {
        icon: ArrowUpDown,
        title: "RELIABLE SERVICE",
        text: "Focused on dependable elevator operation.",
    },
    {
        icon: Wrench,
        title: "PROFESSIONAL WORKMANSHIP",
        text: "Structured and careful service processes.",
    },
    {
        icon: Headset,
        title: "RESPONSIVE SUPPORT",
        text: "Support for maintenance and breakdown requirements.",
    },
    {
        icon: TrendingUp,
        title: "LONG-TERM PERFORMANCE",
        text: "Maintenance focused on keeping elevators operating reliably.",
    },
];

const WHAT_WE_DO = [
    { icon: ArrowUpDown, label: "INSTALLATION" },
    { icon: CalendarCheck, label: "MAINTENANCE" },
    { icon: Zap, label: "BREAKDOWN SERVICE" },
    { icon: Wrench, label: "REPAIR" },
    { icon: Settings, label: "MULTI-BRAND SERVICE SUPPORT" },
];

export default function About() {
    return (
        <>
            <Seo
                title="About Us | K1 ELEVATOR'S — Elevator Company in Patna, Gaya & Ranchi"
                description="K1 ELEVATOR'S is an elevator service company providing lift installation, maintenance, breakdown and repair solutions across Patna, Gaya and Ranchi."
            />

            <PageHero
                eyebrow="About K1 Elevator's"
                title={
                    <>
                        ENGINEERING RELIABILITY.
                        <br />
                        <span className="text-[#60A5FA]">MOVING PEOPLE.</span>
                    </>
                }
                subtitle="K1 ELEVATOR'S is an elevator service company focused on providing dependable lift installation, maintenance and breakdown solutions."
                image={IMG.team}
                testid="about-hero-heading"
            />

            {/* Intro */}
            <section className="mx-auto max-w-4xl px-5 py-20 text-center sm:px-8">
                <Reveal>
                    <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
                        We work with residential, commercial and other properties requiring safe
                        and reliable vertical transportation. Our services cover new installations,
                        preventive maintenance, breakdown troubleshooting and repair.
                    </p>
                </Reveal>
            </section>

            {/* Our Approach */}
            <section className="bg-[#F0F5FD] py-24">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal className="max-w-2xl">
                        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                            How We Work
                        </p>
                        <h2 className="mt-4 text-3xl font-bold tracking-tight text-[#0B1528] sm:text-4xl">
                            OUR APPROACH
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {APPROACH.map((a, i) => (
                            <Reveal
                                key={a.title}
                                delay={i * 80}
                                className="group rounded-2xl border border-slate-200/90 bg-white p-7 shadow-[0_4px_20px_rgb(15,23,42,0.04)] transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_16px_40px_rgb(37,99,235,0.12)]"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F0F5FD] text-[#1D4ED8] transition-colors duration-300 group-hover:bg-[#1D4ED8] group-hover:text-white">
                                    <a.icon className="h-6 w-6" />
                                </div>
                                <h3 className="mt-5 text-sm font-extrabold tracking-wide text-[#0B1528]">
                                    {a.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-500">{a.text}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* What We Do */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
                <div className="grid items-center gap-14 lg:grid-cols-2">
                    <div>
                        <Reveal>
                            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                                Capabilities
                            </p>
                            <h2 className="mt-4 text-3xl font-bold tracking-tight text-[#0B1528] sm:text-4xl">
                                WHAT WE DO
                            </h2>
                        </Reveal>
                        <div className="mt-10 space-y-1">
                            {WHAT_WE_DO.map((item, i) => (
                                <Reveal
                                    key={item.label}
                                    delay={i * 70}
                                    className="group flex items-center gap-5 border-b border-slate-200 py-5 transition-colors duration-200 hover:bg-[#F8FAFC]"
                                >
                                    <span className="w-10 text-2xl font-extrabold text-slate-300 transition-colors duration-200 group-hover:text-[#2563EB]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F0F5FD] text-[#1D4ED8] transition-colors duration-300 group-hover:bg-[#1D4ED8] group-hover:text-white">
                                        <item.icon className="h-5 w-5" />
                                    </span>
                                    <span className="text-base font-extrabold tracking-wide text-[#0B1528]">
                                        {item.label}
                                    </span>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <Reveal delay={120} className="relative">
                        <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_rgb(11,21,40,0.18)]">
                            <img
                                src={IMG.capsule}
                                alt="Modern panoramic elevators on a premium building facade"
                                loading="lazy"
                                className="aspect-[4/5] w-full object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-6 left-6 rounded-2xl bg-[#0B1528] px-6 py-5 text-white shadow-xl">
                            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#60A5FA]">
                                Residential &amp; Commercial
                            </p>
                            <p className="mt-1 text-lg font-extrabold">Vertical Transportation</p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Locations */}
            <section className="relative overflow-hidden bg-[#0B1528] py-24">
                <div
                    className="absolute inset-0 opacity-[0.12]"
                    style={{
                        backgroundImage:
                            "radial-gradient(rgba(96,165,250,0.6) 1px, transparent 1px)",
                        backgroundSize: "26px 26px",
                    }}
                />
                <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal className="max-w-2xl">
                        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#60A5FA]">
                            Bihar &amp; Jharkhand
                        </p>
                        <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                            OUR LOCATIONS
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-6 md:grid-cols-3">
                        {LOCATIONS.map((loc, i) => (
                            <Reveal
                                key={loc.city}
                                delay={i * 100}
                                className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-md transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 hover:border-[#2563EB]/60 hover:bg-white/10"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2563EB]/20 text-[#60A5FA]">
                                    <MapPin className="h-6 w-6" />
                                </div>
                                <h3 className="mt-5 text-xl font-extrabold tracking-wide text-white">
                                    {loc.city}
                                </h3>
                                <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA]">
                                    {loc.state}
                                </p>
                                <p className="mt-3 text-sm leading-relaxed text-slate-300">{loc.short}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Commitment */}
            <section className="mx-auto max-w-4xl px-5 py-24 text-center sm:px-8">
                <Reveal>
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                        Our Commitment
                    </p>
                    <h2 className="mt-4 text-3xl font-bold tracking-tight text-[#0B1528] sm:text-4xl">
                        OUR COMMITMENT
                    </h2>
                    <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                        &ldquo;Every elevator is different. Our focus is to understand the
                        equipment, identify the issue and provide practical service focused on
                        safety, reliability and performance.&rdquo;
                    </p>
                    <Link
                        to="/contact"
                        data-testid="about-commitment-cta-btn"
                        className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-8 py-3.5 text-sm font-bold text-white shadow-[0_4px_16px_rgba(37,99,235,0.35)] transition-colors duration-200 hover:bg-[#1E40AF]"
                    >
                        TALK TO K1 ELEVATOR&rsquo;S <ArrowRight className="h-4 w-4" />
                    </Link>
                </Reveal>
            </section>
        </>
    );
}
